<template>
  <modal-card title="Merge Contacts" fixed @close="$emit('close')">
    <div class="pa-4">
      <p>
        Merge two contact records by selecting which one to keep.<br />
        The data from the merge contact will be combined into the kept contact.
      </p>

      <p>
        <strong>Kept Contact</strong>: This contact will remain and receive all
        merged information.<br />
        <strong>Merge Contact</strong>: This contact will be removed after the
        merge, and its data will be transferred.
      </p>

      <v-row class="merge-contacts-select">
        <v-col cols="6" sm="6" class="pr-5 pl-2">
          <select-contact-field
            label="Kept Contact"
            :title="''"
            :hideAddButton="true"
            :disabled="isContactSelectDisabled(kept_id)"
            v-model="kept_id"
            @input="fetchContact"
          />

          <v-icon
            class="swap-contacts-icon"
            @click="swapContacts"
            color="appMain"
            >swap_horiz</v-icon
          >
        </v-col>
        <v-col cols="6" sm="6" class="pl-5 pr-2">
          <select-contact-field
            label="Merge Contact"
            :title="''"
            :hideAddButton="true"
            :disabled="isContactSelectDisabled(merge_id)"
            v-model="merge_id"
            @input="fetchContact"
          />
        </v-col>
      </v-row>

      <v-fade-transition>
        <contact-merge-table
          v-if="keptContact && mergingContact"
          :keptContact="keptContact"
          :mergeContact="mergingContact"
          :loading="loading"
          class="mt-3"
        />
      </v-fade-transition>
    </div>

    <template #actions>
      <v-btn
        color="appMain"
        :disabled="isMergeDisabled"
        @click="openMergeConfirmation"
      >
        Merge
      </v-btn>
    </template>
  </modal-card>
</template>

<script>
import { mapActions } from 'vuex';

import cloneDeep from 'lodash/cloneDeep';

import SelectContactField from '@/views/conversations/threads/SelectContactField.vue';
import ContactMergeTable from '@/views/contacts/merge/ContactMergeTable.vue';

export default {
  name: 'ContactMergeModal',
  components: {
    SelectContactField,
    ContactMergeTable,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      kept_id: null,
      merge_id: null,
      tempContact: null,
      loading: false,
    };
  },
  computed: {
    mergingContact() {
      return this.getContact(this.merge_id);
    },

    keptContact() {
      return this.getContact(this.kept_id);
    },

    isMergeDisabled() {
      return !this.kept_id || !this.merge_id || this.kept_id === this.merge_id;
    },
  },
  methods: {
    ...mapActions(['mergeContact', 'loadSingleContact']),

    isContactSelectDisabled(contactId) {
      return contactId === this.contact.con_id;
    },

    swapContacts() {
      const temp = this.kept_id;
      this.kept_id = this.merge_id;
      this.merge_id = temp;
    },

    getContact(contactId) {
      if (contactId === this.contact.con_id) {
        return this.contact;
      }

      if (contactId === this.tempContact?.con_id) {
        return this.tempContact;
      }

      return null;
    },

    async fetchContact(contactId) {
      if (!contactId || this.getContact(contactId)) {
        return;
      }

      try {
        this.loading = true;

        const contact = await this.loadSingleContact(contactId);
        this.tempContact = cloneDeep(contact);
      } finally {
        this.loading = false;
      }
    },

    async mergeChosenContacts(dry_run = false) {
      const { kept_id, merge_id } = this;

      if (!kept_id || !merge_id) {
        return;
      }

      await this.mergeContact({
        kept_id,
        merge_id,
        dry_run,
      });

      if (dry_run) return;

      this.$emit('merged', {
        kept_id,
        merge_id,
      });

      // if (kept_id !== this.contact.con_id) {
      //   if (this.$route.name === 'contact-edit') {
      //     this.$router.push({
      //       name: 'contact-edit',
      //       params: { id: kept_id },
      //     });
      //   }
      // }

      this.$emit('close');
    },

    setInitialKept() {
      const { con_id } = this.contact;
      if (con_id) {
        this.kept_id = con_id;
      }
    },

    openMergeConfirmation() {
      this.openConfirmationModal({
        title: 'Merge Contacts Confirmation',
        text: 'Are you sure you want to merge these contacts?<br /> This action cannot be undone.',
        width: '400px',
        onConfirm: () => {
          this.mergeChosenContacts(false);
        },
      });
    },
  },
  mounted() {
    this.setInitialKept();
  },
};
</script>

<style lang="less">
.merge-contacts-select > div {
  position: relative;
}
.swap-contacts-icon {
  position: absolute !important;
  top: 26px;
  right: -14px;
  z-index: 1;
}
</style>
